"use client";

import { useEffect, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import {
  archiveSiteWorkspace,
  createSiteWorkspace,
  fetchSiteWorkspaces,
  type SiteWorkspace,
} from "@/lib/siteWorkspaces";
import { APP_RESUME_EVENT } from "@/lib/appLifecycle";

type ViewMode = "grid" | "list";

const canManageSites = (role: string) => role === "OWNER" || role === "ADMIN";
const roleLabel = (role: string) =>
  role === "OWNER"
    ? "소유자"
    : role === "ADMIN"
      ? "관리자"
      : role === "EDITOR"
        ? "편집자"
        : "열람자";

const formatDate = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("ko-KR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
};

function BuildingIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 21h18" />
      <path d="M6 21V4a1 1 0 0 1 1-1h7a1 1 0 0 1 1 1v17" />
      <path d="M15 8h3a1 1 0 0 1 1 1v12" />
      <path d="M9 7h2M9 11h2M9 15h2" />
    </svg>
  );
}

export default function ProjectsPage() {
  const router = useRouter();
  const [projects, setProjects] = useState<SiteWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createLocation, setCreateLocation] = useState("");
  const [createManager, setCreateManager] = useState("");
  const [createStartDate, setCreateStartDate] = useState("");
  const [createEndDate, setCreateEndDate] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");

  const [archiveTarget, setArchiveTarget] = useState<SiteWorkspace | null>(
    null,
  );
  const [archiving, setArchiving] = useState(false);

  async function load() {
    try {
      setLoadError("");
      const nextProjects = await fetchSiteWorkspaces();
      setProjects(nextProjects);
    } catch (error) {
      setLoadError(
        error instanceof Error
          ? error.message
          : "현장 프로젝트를 불러오지 못했습니다.",
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const refresh = () => void load();
    window.addEventListener(APP_RESUME_EVENT, refresh);
    window.addEventListener("projects-updated", refresh);
    return () => {
      window.removeEventListener(APP_RESUME_EVENT, refresh);
      window.removeEventListener("projects-updated", refresh);
    };
  }, []);

  const filteredProjects = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase("ko-KR");
    if (!query) return projects;
    return projects.filter((project) =>
      [
        project.name,
        roleLabel(project.role),
        project.location,
        project.siteManager,
        project.description,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("ko-KR")
        .includes(query),
    );
  }, [projects, searchQuery]);

  const openCreate = () => {
    setCreateError("");
    setShowCreateModal(true);
  };

  async function handleCreate(event: React.FormEvent) {
    event.preventDefault();
    if (!createName.trim()) return;
    if (createStartDate && createEndDate && createStartDate > createEndDate) {
      setCreateError("착공일은 준공일보다 늦을 수 없습니다.");
      return;
    }
    setCreating(true);
    setCreateError("");
    try {
      const created = await createSiteWorkspace({
        name: createName.trim(),
        description: createDesc.trim() || null,
        location: createLocation.trim() || null,
        siteManager: createManager.trim() || null,
        startDate: createStartDate || null,
        endDate: createEndDate || null,
      });
      setShowCreateModal(false);
      setCreateName("");
      setCreateLocation("");
      setCreateManager("");
      setCreateStartDate("");
      setCreateEndDate("");
      setCreateDesc("");
      window.dispatchEvent(new Event("projects-updated"));
      router.push(`/projects/${created.id}`);
    } catch (error) {
      setCreateError(
        error instanceof Error
          ? error.message
          : "현장 프로젝트 생성에 실패했습니다.",
      );
    } finally {
      setCreating(false);
    }
  }

  async function handleArchive() {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      await archiveSiteWorkspace(archiveTarget.id);
      setArchiveTarget(null);
      window.dispatchEvent(new Event("projects-updated"));
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "프로젝트를 보관하지 못했습니다.",
      );
    } finally {
      setArchiving(false);
    }
  }

  return (
    <div className="relative flex h-full w-full flex-col overflow-y-auto bg-white pt-12 custom-scrollbar md:pt-0 dark:bg-black">
      <div className="hidden w-full px-6 pt-6 sm:flex sm:justify-end">
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center justify-center gap-1.5 rounded-xl bg-seexr-solid px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-seexr-solid/90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          새 현장 프로젝트
        </button>
      </div>

      <div className="mx-auto mb-6 mt-1 flex w-full max-w-[1000px] flex-wrap items-center gap-4 px-6 pt-8 sm:mt-9 sm:pt-0">
        <h1 className="text-[28px] font-black leading-[34px] text-gray-900 dark:text-gray-100">
          현장 프로젝트
        </h1>
        <div className="w-full sm:ms-auto sm:w-[300px]">
          <div className="relative w-full">
            <svg
              className="pointer-events-none absolute start-3 top-1/2 h-4.5 w-4.5 -translate-y-1/2 text-gray-400"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="현장 검색..."
              className="h-10 w-full rounded-full border border-gray-200 bg-gray-50 ps-9 pe-8 text-[14px] text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-seexr-primary focus:ring-1 focus:ring-seexr-primary dark:border-[#2a2a2e] dark:bg-[#111113] dark:text-gray-100 dark:focus:border-seexr-primary"
            />
          </div>
        </div>
      </div>

      <section className="w-full">
        <div className="sticky top-0 z-20 w-full border-b border-gray-100 bg-white/80 backdrop-blur-md dark:border-[#2a2a2e] dark:bg-black/80">
          <div className="mx-auto flex w-full max-w-[1000px] px-6">
            <div className="flex min-h-[52px] w-full items-center gap-4 py-2.5">
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-gray-600 dark:text-gray-400">
                {filteredProjects.length === projects.length
                  ? `총 ${projects.length}개의 현장`
                  : `${filteredProjects.length}개 검색 결과`}
              </span>
              <button
                type="button"
                onClick={openCreate}
                className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-bold text-seexr-primary transition-colors hover:bg-seexr-solid/10 disabled:opacity-40 sm:hidden"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                새 현장
              </button>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  aria-label="갤러리 보기"
                  title="갤러리 보기"
                  onClick={() => setViewMode("grid")}
                  className={`flex size-9 items-center justify-center rounded-full transition-colors ${
                    viewMode === "grid"
                      ? "bg-gray-100 text-gray-900 dark:bg-[#2a2a2e] dark:text-white"
                      : "text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"
                  }`}
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="3" y="3" width="7" height="7" />
                    <rect x="14" y="3" width="7" height="7" />
                    <rect x="14" y="14" width="7" height="7" />
                    <rect x="3" y="14" width="7" height="7" />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="리스트 보기"
                  title="리스트 보기"
                  onClick={() => setViewMode("list")}
                  className={`flex size-9 items-center justify-center rounded-full transition-colors ${
                    viewMode === "list"
                      ? "bg-gray-100 text-gray-900 dark:bg-[#2a2a2e] dark:text-white"
                      : "text-gray-500 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-gray-800"
                  }`}
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="8" y1="6" x2="21" y2="6" />
                    <line x1="8" y1="12" x2="21" y2="12" />
                    <line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" />
                    <line x1="3" y1="12" x2="3.01" y2="12" />
                    <line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1000px] px-6 pb-16 pt-4 md:pb-12">
          {loading ? (
            <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((item) => (
                <div
                  key={item}
                  className="h-[210px] animate-pulse rounded-2xl border border-gray-100 bg-gray-50 dark:border-[#252529] dark:bg-[#1c1c1f]"
                />
              ))}
            </div>
          ) : loadError && projects.length === 0 ? (
            <div className="flex min-h-[360px] flex-col items-center justify-center px-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-500 dark:bg-red-500/10">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 8v5M12 17h.01" />
                </svg>
              </div>
              <p className="mt-4 text-sm font-black text-gray-800 dark:text-gray-100">
                {loadError}
              </p>
              <button
                type="button"
                onClick={() => {
                  setLoading(true);
                  void load();
                }}
                className="mt-4 rounded-xl border border-gray-200 px-4 py-2 text-xs font-black text-gray-600 hover:bg-gray-50 dark:border-[#313136] dark:text-gray-300 dark:hover:bg-white/5"
              >
                다시 불러오기
              </button>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="flex min-h-[360px] flex-col items-center justify-center px-6 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-seexr-primary/15 bg-seexr-solid/10 text-seexr-primary">
                <BuildingIcon className="h-7 w-7" />
              </div>
              <p className="mt-4 text-[15px] font-black text-gray-800 dark:text-gray-100">
                {searchQuery.trim()
                  ? "검색 결과가 없습니다."
                  : "아직 현장 프로젝트가 없습니다."}
              </p>
              <p className="mt-1 max-w-md text-[12px] leading-5 text-gray-400">
                {searchQuery.trim()
                  ? "다른 현장명이나 주소로 검색해보세요."
                  : "첫 현장을 만들면 위험성평가와 현장 자료가 자동으로 이 프로젝트에 쌓입니다."}
              </p>
              {!searchQuery.trim() && (
                <button
                  type="button"
                  onClick={openCreate}
                  className="mt-5 rounded-xl bg-seexr-solid px-4 py-2.5 text-xs font-black text-white"
                >
                  첫 현장 만들기
                </button>
              )}
            </div>
          ) : viewMode === "grid" ? (
            <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredProjects.map((project) => {
                const start = formatDate(project.startDate);
                const end = formatDate(project.endDate);
                const manageable = canManageSites(project.role);
                return (
                  <article
                    key={project.id}
                    className="group relative flex h-[220px] cursor-pointer flex-col rounded-[28px] border border-gray-200 bg-white p-7 shadow-sm transition-colors hover:border-seexr-primary/40 dark:border-[#2a2a2e] dark:bg-[#161618]"
                    onClick={() => router.push(`/projects/${project.id}`)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-seexr-solid/10 text-seexr-primary">
                        <BuildingIcon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <h2 className="truncate text-[15px] font-black text-gray-900 dark:text-white">
                            {project.name}
                          </h2>
                          <span className="shrink-0 rounded-full bg-seexr-solid/10 px-2 py-0.5 text-[9px] font-black text-seexr-primary">
                            현장
                          </span>
                        </div>
                        <p className="mt-1 truncate text-[11px] font-semibold text-gray-400">
                          {roleLabel(project.role)}
                        </p>
                      </div>
                      {manageable && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setArchiveTarget(project);
                          }}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-300 opacity-0 transition hover:bg-gray-100 hover:text-red-500 group-hover:opacity-100 dark:hover:bg-white/5"
                          aria-label="현장 보관"
                        >
                          <svg
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            aria-hidden="true"
                          >
                            <path d="M4 7h16M6 7l1 14h10l1-14M9 7V4h6v3M10 11v6M14 11v6" />
                          </svg>
                        </button>
                      )}
                    </div>

                    <div className="mt-5 space-y-2 text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                      <div className="flex min-w-0 items-center gap-2">
                        <svg
                          className="h-3.5 w-3.5 shrink-0 text-gray-400"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
                          <circle cx="12" cy="10" r="2" />
                        </svg>
                        <span className="truncate">
                          {project.location || "주소 미등록"}
                        </span>
                      </div>
                      <div className="flex min-w-0 items-center gap-2">
                        <svg
                          className="h-3.5 w-3.5 shrink-0 text-gray-400"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="8" r="4" />
                          <path d="M4 21a8 8 0 0 1 16 0" />
                        </svg>
                        <span className="truncate">
                          {project.siteManager || "현장 담당자 미등록"}
                        </span>
                      </div>
                    </div>

                    <div className="mt-auto flex items-center justify-between border-t border-gray-100 pt-4 text-[10px] font-bold text-gray-400 dark:border-[#2c2c30]">
                      <span>
                        {start || end
                          ? `${start || "미정"} – ${end || "미정"}`
                          : "공사기간 미등록"}
                      </span>
                      <span className="inline-flex items-center gap-1 text-seexr-primary">
                        열기
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          aria-hidden="true"
                        >
                          <path d="m9 18 6-6-6-6" />
                        </svg>
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="mt-2">
              <div className="hidden border-b border-gray-200 px-3 text-[11px] font-bold text-gray-400 sm:grid sm:min-h-[40px] sm:grid-cols-[minmax(0,1fr)_minmax(0,140px)_150px_40px] sm:items-center sm:gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,150px)_minmax(0,190px)_minmax(0,140px)_170px_40px] dark:border-[#2a2a2e]">
                <div>현장</div>
                <div>권한</div>
                <div className="hidden lg:block">위치</div>
                <div className="hidden lg:block">담당자</div>
                <div>공사기간</div>
                <div />
              </div>
              <div className="space-y-1.5 pt-2">
                {filteredProjects.map((project) => {
                  const start = formatDate(project.startDate);
                  const end = formatDate(project.endDate);
                  const manageable = canManageSites(project.role);
                  return (
                    <article
                      key={project.id}
                      onClick={() => router.push(`/projects/${project.id}`)}
                      className="group grid min-h-[62px] cursor-pointer grid-cols-[minmax(0,1fr)_40px] items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 transition-colors hover:border-seexr-primary/20 hover:bg-seexr-solid/[0.035] sm:grid-cols-[minmax(0,1fr)_minmax(0,140px)_150px_40px] sm:gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,150px)_minmax(0,190px)_minmax(0,140px)_170px_40px] dark:hover:bg-seexr-solid/[0.06]"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-seexr-solid/10 text-seexr-primary">
                          <BuildingIcon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <div className="truncate text-[13px] font-black text-gray-800 dark:text-gray-100">
                            {project.name}
                          </div>
                          <div className="mt-0.5 truncate text-[10px] font-semibold text-gray-400 sm:hidden">
                            {roleLabel(project.role)} ·{" "}
                            {project.location || "주소 미등록"}
                          </div>
                        </div>
                      </div>
                      <div className="hidden truncate text-[11px] font-semibold text-gray-500 sm:block dark:text-gray-400">
                        {roleLabel(project.role)}
                      </div>
                      <div className="hidden truncate text-[11px] font-semibold text-gray-500 lg:block dark:text-gray-400">
                        {project.location || "주소 미등록"}
                      </div>
                      <div className="hidden truncate text-[11px] font-semibold text-gray-500 lg:block dark:text-gray-400">
                        {project.siteManager || "미등록"}
                      </div>
                      <div className="hidden truncate text-[10px] font-semibold text-gray-400 sm:block">
                        {start || end
                          ? `${start || "미정"} – ${end || "미정"}`
                          : "기간 미등록"}
                      </div>
                      <div className="flex items-center justify-end">
                        {manageable ? (
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              setArchiveTarget(project);
                            }}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-300 opacity-0 transition-colors hover:bg-red-50 hover:text-red-500 group-hover:opacity-100 dark:hover:bg-red-950/20"
                            aria-label="현장 보관"
                          >
                            <svg
                              width="15"
                              height="15"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              aria-hidden="true"
                            >
                              <path d="M4 7h16M6 7l1 14h10l1-14M9 7V4h6v3M10 11v6M14 11v6" />
                            </svg>
                          </button>
                        ) : (
                          <svg
                            className="text-gray-300 group-hover:text-seexr-primary"
                            width="15"
                            height="15"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.3"
                            aria-hidden="true"
                          >
                            <path d="m9 18 6-6-6-6" />
                          </svg>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {showCreateModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target && !creating)
              setShowCreateModal(false);
          }}
        >
          <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-[24px] border border-gray-200 bg-white p-6 shadow-2xl dark:border-[#303036] dark:bg-[#18181b]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-gray-950 dark:text-white">
                  새 현장 프로젝트
                </h2>
                <p className="mt-1 text-[12px] leading-5 text-gray-400">
                  이 현장에 생성되는 위험성평가와 파일, 작업자 전파 기록이
                  하나의 Workspace에 연결됩니다.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-300"
                aria-label="닫기"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  aria-hidden="true"
                >
                  <path d="m6 6 12 12M18 6 6 18" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreate} className="mt-6 space-y-4">
              <label className="block">
                <span className="mb-1.5 block text-[11px] font-black text-gray-500">
                  현장명 *
                </span>
                <input
                  value={createName}
                  onChange={(event) => setCreateName(event.target.value)}
                  required
                  maxLength={200}
                  placeholder="예: 판교 데이터센터 신축공사"
                  className="h-11 w-full rounded-xl border border-gray-200 bg-transparent px-3.5 text-[13px] font-bold text-gray-900 outline-none focus:border-seexr-primary focus:ring-2 focus:ring-seexr-primary/10 dark:border-[#313136] dark:text-white"
                />
              </label>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-[11px] font-black text-gray-500">
                    현장 위치
                  </span>
                  <input
                    value={createLocation}
                    onChange={(event) => setCreateLocation(event.target.value)}
                    maxLength={200}
                    placeholder="주소 또는 현장 위치"
                    className="h-11 w-full rounded-xl border border-gray-200 bg-transparent px-3.5 text-[13px] font-medium text-gray-900 outline-none focus:border-seexr-primary dark:border-[#313136] dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[11px] font-black text-gray-500">
                    현장 담당자
                  </span>
                  <input
                    value={createManager}
                    onChange={(event) => setCreateManager(event.target.value)}
                    maxLength={100}
                    placeholder="예: 김OO 소장"
                    className="h-11 w-full rounded-xl border border-gray-200 bg-transparent px-3.5 text-[13px] font-medium text-gray-900 outline-none focus:border-seexr-primary dark:border-[#313136] dark:text-white"
                  />
                </label>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1.5 block text-[11px] font-black text-gray-500">
                    착공일
                  </span>
                  <input
                    type="date"
                    value={createStartDate}
                    onChange={(event) => setCreateStartDate(event.target.value)}
                    className="h-11 w-full rounded-xl border border-gray-200 bg-transparent px-3.5 text-[13px] font-medium text-gray-900 outline-none focus:border-seexr-primary dark:border-[#313136] dark:text-white"
                  />
                </label>
                <label className="block">
                  <span className="mb-1.5 block text-[11px] font-black text-gray-500">
                    준공일
                  </span>
                  <input
                    type="date"
                    value={createEndDate}
                    onChange={(event) => setCreateEndDate(event.target.value)}
                    className="h-11 w-full rounded-xl border border-gray-200 bg-transparent px-3.5 text-[13px] font-medium text-gray-900 outline-none focus:border-seexr-primary dark:border-[#313136] dark:text-white"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1.5 block text-[11px] font-black text-gray-500">
                  현장 설명
                </span>
                <textarea
                  value={createDesc}
                  onChange={(event) => setCreateDesc(event.target.value)}
                  rows={3}
                  placeholder="공사의 범위, 주요 공종, 현장 특성 등을 적어두면 프로젝트를 구분하기 쉽습니다."
                  className="w-full resize-none rounded-xl border border-gray-200 bg-transparent px-3.5 py-3 text-[13px] font-medium text-gray-900 outline-none focus:border-seexr-primary dark:border-[#313136] dark:text-white"
                />
              </label>

              {createError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs font-bold text-red-600 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                  {createError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                  className="h-10 rounded-xl border border-gray-200 px-4 text-xs font-black text-gray-500 dark:border-[#313136] dark:text-gray-300"
                >
                  취소
                </button>
                <button
                  type="submit"
                  disabled={creating || !createName.trim()}
                  className="h-10 rounded-xl bg-seexr-solid px-5 text-xs font-black text-white disabled:opacity-40"
                >
                  {creating ? "만드는 중…" : "현장 프로젝트 만들기"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {archiveTarget && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/55 p-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.currentTarget === event.target && !archiving)
              setArchiveTarget(null);
          }}
        >
          <div className="w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-[#303036] dark:bg-[#18181b]">
            <h2 className="text-lg font-black text-gray-950 dark:text-white">
              현장 프로젝트 보관
            </h2>
            <p className="mt-2 text-[13px] leading-5 text-gray-500 dark:text-gray-400">
              <b className="text-gray-800 dark:text-gray-200">
                {archiveTarget.name}
              </b>
              을 활성 현장 목록에서 보관할까요? 기존 데이터는 삭제하지 않습니다.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setArchiveTarget(null)}
                disabled={archiving}
                className="h-10 rounded-xl border border-gray-200 px-4 text-xs font-black text-gray-500 dark:border-[#313136]"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => void handleArchive()}
                disabled={archiving}
                className="h-10 rounded-xl bg-red-600 px-4 text-xs font-black text-white disabled:opacity-40"
              >
                {archiving ? "보관 중…" : "보관하기"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
